/**
 * IconPicker Component
 *
 * Main icon picker component with search, category tabs, and virtualized grid.
 * Provides a comprehensive interface for selecting icons for attack path nodes.
 *
 * Features:
 * - Search filtering with screen reader announcements
 * - Category tabs for organizational filtering
 * - Virtualized grid for performance with 100+ icons
 * - Non-blocking category transitions with useTransition
 * - Full keyboard navigation support
 */

import { useCallback, useMemo, useState } from 'react';

import {
  useIconCategoryTransition,
  useIconSearch,
} from '@/sections/attacks/attackPaths/components/icons/hooks';
import {
  getAllIcons,
  type IconDefinition,
} from '@/sections/attacks/attackPaths/components/icons/registry';
import { scopeIconId } from '@/sections/attacks/attackPaths/components/icons/utils/iconSelectionUtils';

import {
  CATEGORY_ICON_IDS,
  CATEGORY_TO_SOURCES,
  type CategoryId,
  DEFAULT_CATEGORY,
  ICON_CATEGORIES,
  type ValidIconId,
} from './categories.config';
import { IconCategoryTabs } from './IconCategoryTabs';
import { IconGrid } from './IconGrid';
import { IconSearchInput } from './IconSearchInput';

/** Selection data passed when an icon is selected */
export interface IconSelection {
  /** Icon identifier (e.g., 'iot:WiFi', or scoped: 'internet::infrastructure:Server') */
  id: string;
  /** Human-readable label (e.g., 'WiFi') */
  label: string;
  /** Icon source category (e.g., 'iot', 'aws') */
  source: string;
  /** Category tab where icon was selected (e.g., 'internet', 'intranet') */
  pickerCategory: string;
}

interface IconPickerProps {
  /** Currently selected icon id */
  value: string | undefined;
  /** Callback when icon is selected */
  onChange: (selection: IconSelection) => void;
  /** Optional description text to display above the picker */
  description?: string;
}

/**
 * Main IconPicker component.
 *
 * Combines search, category filtering, and virtualized grid for a complete
 * icon selection experience.
 *
 * @example
 * ```tsx
 * <IconPicker
 *   value={formData.iconOverride}
 *   onChange={(iconId) => setFormData({ ...formData, iconOverride: iconId })}
 * />
 * ```
 */
export function IconPicker({ value, onChange, description }: IconPickerProps) {
  const [selectedCategory, setSelectedCategory] = useState(DEFAULT_CATEGORY);

  // Get all icons from the registry system (AWS, Azure, GCP, AD, Lucide, Custom)
  const allIcons = useMemo(() => getAllIcons(), []);

  // Development-mode validation: Warn about invalid icon IDs in CATEGORY_ICON_IDS
  // eslint-disable-next-line no-undef
  if (process.env.NODE_ENV === 'development') {
    const allIconIds = allIcons.map(i => i.id);
    Object.entries(CATEGORY_ICON_IDS).forEach(([category, iconIds]) => {
      iconIds?.forEach(id => {
        if (!allIconIds.includes(id)) {
          console.warn(
            `[IconPicker] Invalid icon ID in ${category} category: ${id}`
          );
        }
      });
    });
  }

  // Category filtering based on attack surface taxonomy
  const categoryFilteredIcons = useMemo(() => {
    if (selectedCategory === 'all') return allIcons;

    const sourcesToInclude =
      CATEGORY_TO_SOURCES[
        selectedCategory as keyof typeof CATEGORY_TO_SOURCES
      ] ?? [];
    const specificIconIds =
      CATEGORY_ICON_IDS[selectedCategory as keyof typeof CATEGORY_ICON_IDS] ??
      [];

    // If no sources or specific icons defined, category is empty
    if (sourcesToInclude.length === 0 && specificIconIds.length === 0) {
      return [];
    }

    return allIcons.filter(icon => {
      const iconCategory = icon.category ?? '';
      const iconId = icon.id ?? '';

      // Check if icon's category matches any source
      if (sourcesToInclude.includes(iconCategory)) {
        return true;
      }
      // Check if icon ID is specifically included
      // Type assertion is safe because runtime validation catches invalid IDs in dev mode
      if (specificIconIds.includes(iconId as ValidIconId)) {
        return true;
      }
      return false;
    });
  }, [allIcons, selectedCategory]);

  // Search filtering
  const { searchTerm, setSearchTerm, filteredIcons, isFiltering, resultCount } =
    useIconSearch(categoryFilteredIcons, selectedCategory);

  // Non-blocking category transitions
  const { isPending, selectCategory } = useIconCategoryTransition();

  const handleCategoryChange = useCallback(
    (categoryId: string) => {
      selectCategory(categoryId, () => {
        setSelectedCategory(categoryId as CategoryId);
        setSearchTerm(''); // Clear search when changing categories
      });
    },
    [selectCategory, setSearchTerm]
  );

  const handleSelectIcon = useCallback(
    (icon: IconDefinition) => {
      // Scope shared icons with category prefix to prevent cross-category highlighting
      const scopedId = scopeIconId(icon.id, selectedCategory);
      onChange({
        id: scopedId,
        label: icon.label,
        source: icon.category,
        pickerCategory: selectedCategory,
      });
    },
    [onChange, selectedCategory]
  );

  return (
    <div className="flex flex-col gap-4" data-testid="icon-picker">
      {description && (
        <p className="text-chariot-text-secondary text-sm">{description}</p>
      )}
      <IconSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        resultCount={resultCount}
        isFiltering={isFiltering}
      />

      <IconCategoryTabs
        categories={ICON_CATEGORIES}
        selectedCategory={selectedCategory}
        onSelectCategory={handleCategoryChange}
        isPending={isPending}
      />

      <IconGrid
        icons={filteredIcons}
        selectedIcon={value}
        onSelect={handleSelectIcon}
        categoryId={selectedCategory}
        searchTerm={searchTerm}
        onClearSearch={() => setSearchTerm('')}
      />
    </div>
  );
}
